/**
 * Phil platform — Administrator (FIL) types and UI config.
 *
 * All data (pipeline, KPIs, application details, reports) comes exclusively
 * from the backend via:
 *   GET /api/internal/applications        → { kpis, applications }
 *   GET /api/internal/applications/{id}   → InternalDetail
 *   GET /api/internal/reports             → { reports }
 */

// Portal-native statuses plus the F6S-export statuses carried by historical cohorts.
export type AppStatus =
  | 'Submitted' | 'In Progress' | 'Under Review' | 'Draft' | 'Finalized' | 'Imported';

export type SelectionOutcome = 'Selected' | 'Not Selected' | 'In Progress';

export interface AssignedFi {
  id: string;
  shortName: string;
}

// An admin-configured answer-derived column (2027+ layout only).
export interface PipelineColumnDef {
  questionId: number;
  label: string;
}

export interface PipelineRow {
  id: string;
  company: string;
  industry: string;
  avgScore: number | null;   // seeded pre-screen ("invite") rating
  evalAvg: number | null;    // live cross-FI evaluation average (FiEvaluation)
  submitted: string;
  dateCompleted: string;  // submission date, formatted ("Jun 10, 2026") or "—"
  lastEdited: string;     // last answer save / submit, formatted or "—"
  status: string;
  outcome: SelectionOutcome | string;
  archived: boolean;         // admin soft-hidden from the default pipeline view
  assignedFis: AssignedFi[];
  // 2027+ layout only: per-evaluator-type score breakdown (pooled by FI.industry).
  totalAvg?: number | null;  // pooled mean of all evaluators
  fiAvg?: number | null;     // FI-partner evaluators (excl. VC + EIR/FIL)
  vcAvg?: number | null;     // Venture Capitalist evaluators
  eirAvg?: number | null;    // EIR/FIL (Executive In Residence) evaluators
  // 2027+ layout only: configured column values, keyed by question id (string).
  dynamicCols?: Record<string, string>;
}

export interface PipelineKPIs {
  total: number;
  submitted: number;
  inProgress: number;
  awaitingReview: number;
}

export const STATUS_BADGE: Record<string, string> = {
  Submitted: 'phil-badge phil-badge-complete',
  Finalized: 'phil-badge phil-badge-complete',
  'In Progress': 'phil-badge phil-badge-review',
  'Under Review': 'phil-badge phil-badge-submitted',
  Draft: 'phil-badge phil-badge-draft',
  Imported: 'phil-badge phil-badge-draft',
};

export const OUTCOME_BADGE: Record<string, string> = {
  Selected: 'phil-badge phil-badge-complete',
  'Not Selected': 'phil-badge phil-badge-draft',
  'In Progress': 'phil-badge phil-badge-review',
};

// --- Application detail types -------------------------------------------------

export interface KV { label: string; value: string; }
export interface MatrixRow { area: string; fy24: string; fy25: string; fy26: string; }
export interface Cert { label: string; ok: boolean; }
export interface Chip { label: string; selected: boolean; }
export interface FiScoreRow {
  fi: string; evaluator: string;
  scores: (number | null)[];
  fiAvgRounded: number; fiAvgExact: number;
  notes?: string;
}
export interface HistoryCycle { cycle: string; status: string; scoreRounded: number; scoreExact: number; outcome: string; }
export interface Mentorship { mentor: string; sessions: number; focus: string; }
export interface ActivityEntry { date: string; title: string; detail: string; }

export interface InternalDetail {
  id: string;
  company: string;
  status: AppStatus;
  profile: {
    keyInfo: KV[];
    priorityMatrix: MatrixRow[];
  };
  technology: {
    productOverview: KV[];
    technicalReadiness: KV[];
    explorationOptions: string[];
    certifications: Cert[];
  };
  financials: {
    fundingStage: KV[];
    revenueGrowth: KV[];
  };
  demographics: {
    race: Chip[];
    gender: string;
    identifiesAs: Chip[];
    esg: Cert[];
    internalNote: string;
  };
  fiScores: {
    overallAvg: number;
    byQuestion: { q: string; avg: number }[];
    scored: number;
    pending: number;
    rows: FiScoreRow[];
    crossAvg: number[];
    crossAvgRounded: number;
    crossAvgExact: number;
    guide: string;
  };
  history: {
    cycles: HistoryCycle[];
    mentorship: Mentorship[];
    activityLog: ActivityEntry[];
  };
}

export const SCORE_QUESTION_LABELS = ['Unmet Need', 'Value Proposition', 'Tech Differentiation', 'Team Strength', 'Mentor Fit', 'Interview Invite'];

// --- Reports type -------------------------------------------------------------

export interface ReportCard {
  id: string;
  icon: string;
  title: string;
  description: string;
  lastGenerated: string;
  type: 'analysis' | 'priority-trend' | 'fi-one-pagers' | 'priority-mapping';
  fiSelection?: string[];
}
